import type { WeaponAudioResolver } from '@sstk/weapon-kit';

// Real, verified CC0 gunfire samples: one isolated shot per Sunder archetype,
// trimmed and normalised from OpenGameArt.org's "The Free Firearm Sound
// Library" (Ben Jaszczak et al., CC0 - see sstk-studio-attribution's
// audio/CC0/the-free-firearm-sound-library/License.txt for full sourcing
// detail and the source weapon/take chosen for each), plus StarNinjas' "20
// Sword Sound Effects" (OpenGameArt, CC0) for the Shiv's melee swing/impact
// pair - see sstk-sunder-web's docs/gunsmith/audio.md for the full sourcing
// breakdown of every set, including the six archetypes added alongside the
// original six (breacher/fang/talon/trinity/viper/ripple used to have no
// dedicated sample set of their own and silently reused a sibling
// archetype's audio; the Shiv had none at all). Bundled here the same way
// chassis/component GLBs are (Vite's fingerprinted asset URL convention, see
// catalogue/assets.ts) - Studio's own copy, matching how every consuming app
// bundles its own audio per @sstk/weapon-kit's docs/audio.md.
const BUNDLED_SAMPLE_URLS: Record<string, string> = {
  'sidearm-crack-01': new URL('../assets/audio/sidearm-crack-01.mp3', import.meta.url).href,
  'magnum-crack-01': new URL('../assets/audio/magnum-crack-01.mp3', import.meta.url).href,
  'vector-crack-01': new URL('../assets/audio/vector-crack-01.mp3', import.meta.url).href,
  'assault-crack-01': new URL('../assets/audio/assault-crack-01.mp3', import.meta.url).href,
  'marksman-crack-01': new URL('../assets/audio/marksman-crack-01.mp3', import.meta.url).href,
  'longshot-crack-01': new URL('../assets/audio/longshot-crack-01.mp3', import.meta.url).href,
  'breacher-crack-01': new URL('../assets/audio/breacher-crack-01.mp3', import.meta.url).href,
  'fang-crack-01': new URL('../assets/audio/fang-crack-01.mp3', import.meta.url).href,
  'talon-crack-01': new URL('../assets/audio/talon-crack-01.mp3', import.meta.url).href,
  'trinity-crack-01': new URL('../assets/audio/trinity-crack-01.mp3', import.meta.url).href,
  'viper-crack-01': new URL('../assets/audio/viper-crack-01.mp3', import.meta.url).href,
  'ripple-crack-01': new URL('../assets/audio/ripple-crack-01.mp3', import.meta.url).href,
  // Body and tail layers: a second and third isolated take of the same
  // weapon from the same source recording as the crack layer above (each
  // "prepared" source file contains several real, separately isolated
  // gunshot events, not just one - see the crack sourcing note), trimmed
  // and normalised the same way (ffmpeg silencedetect for real event
  // boundaries, loudnorm, afade for click-free edges). Body is loudnorm'd
  // to sit as close to the crack layer's presence; tail is quieter and
  // longer, capturing more of the natural decay, so all three layer
  // together rather than fighting for the same headroom.
  'sidearm-body-01': new URL('../assets/audio/sidearm-body-01.mp3', import.meta.url).href,
  'magnum-body-01': new URL('../assets/audio/magnum-body-01.mp3', import.meta.url).href,
  'vector-body-01': new URL('../assets/audio/vector-body-01.mp3', import.meta.url).href,
  'assault-body-01': new URL('../assets/audio/assault-body-01.mp3', import.meta.url).href,
  'marksman-body-01': new URL('../assets/audio/marksman-body-01.mp3', import.meta.url).href,
  'longshot-body-01': new URL('../assets/audio/longshot-body-01.mp3', import.meta.url).href,
  'breacher-body-01': new URL('../assets/audio/breacher-body-01.mp3', import.meta.url).href,
  'fang-body-01': new URL('../assets/audio/fang-body-01.mp3', import.meta.url).href,
  'talon-body-01': new URL('../assets/audio/talon-body-01.mp3', import.meta.url).href,
  'trinity-body-01': new URL('../assets/audio/trinity-body-01.mp3', import.meta.url).href,
  'viper-body-01': new URL('../assets/audio/viper-body-01.mp3', import.meta.url).href,
  'ripple-body-01': new URL('../assets/audio/ripple-body-01.mp3', import.meta.url).href,
  'sidearm-tail-01': new URL('../assets/audio/sidearm-tail-01.mp3', import.meta.url).href,
  'magnum-tail-01': new URL('../assets/audio/magnum-tail-01.mp3', import.meta.url).href,
  'vector-tail-01': new URL('../assets/audio/vector-tail-01.mp3', import.meta.url).href,
  'assault-tail-01': new URL('../assets/audio/assault-tail-01.mp3', import.meta.url).href,
  'marksman-tail-01': new URL('../assets/audio/marksman-tail-01.mp3', import.meta.url).href,
  'longshot-tail-01': new URL('../assets/audio/longshot-tail-01.mp3', import.meta.url).href,
  'breacher-tail-01': new URL('../assets/audio/breacher-tail-01.mp3', import.meta.url).href,
  'fang-tail-01': new URL('../assets/audio/fang-tail-01.mp3', import.meta.url).href,
  'talon-tail-01': new URL('../assets/audio/talon-tail-01.mp3', import.meta.url).href,
  'trinity-tail-01': new URL('../assets/audio/trinity-tail-01.mp3', import.meta.url).href,
  'viper-tail-01': new URL('../assets/audio/viper-tail-01.mp3', import.meta.url).href,
  'ripple-tail-01': new URL('../assets/audio/ripple-tail-01.mp3', import.meta.url).href,
  // Melee: a real blade swing and a real blade-contact hit, not a
  // gunshot-derived layer - see docs/gunsmith/audio.md (sstk-sunder-web) for
  // the full sourcing/trim breakdown.
  'shiv-swing-01': new URL('../assets/audio/shiv-swing-01.mp3', import.meta.url).href,
  'shiv-impact-01': new URL('../assets/audio/shiv-impact-01.mp3', import.meta.url).href,
};

/**
 * One real, ready-to-use sample id per sim archetype, for a creator who
 * hasn't picked their own sample yet - not a fixed catalogue (a creator can
 * still type any sampleId; see the wider sourcing note above for where more
 * would come from once Content's own curated library exists).
 */
export const DEFAULT_CRACK_SAMPLE_BY_ARCHETYPE: Record<string, string> = {
  sidearm: 'sidearm-crack-01',
  magnum: 'magnum-crack-01',
  vector: 'vector-crack-01',
  assault: 'assault-crack-01',
  marksman: 'marksman-crack-01',
  longshot: 'longshot-crack-01',
  breacher: 'breacher-crack-01',
  fang: 'fang-crack-01',
  talon: 'talon-crack-01',
  trinity: 'trinity-crack-01',
  viper: 'viper-crack-01',
  ripple: 'ripple-crack-01',
};

/** Same idea as {@link DEFAULT_CRACK_SAMPLE_BY_ARCHETYPE}, for the body layer. */
export const DEFAULT_BODY_SAMPLE_BY_ARCHETYPE: Record<string, string> = {
  sidearm: 'sidearm-body-01',
  magnum: 'magnum-body-01',
  vector: 'vector-body-01',
  assault: 'assault-body-01',
  marksman: 'marksman-body-01',
  longshot: 'longshot-body-01',
  breacher: 'breacher-body-01',
  fang: 'fang-body-01',
  talon: 'talon-body-01',
  trinity: 'trinity-body-01',
  viper: 'viper-body-01',
  ripple: 'ripple-body-01',
};

/** Same idea as {@link DEFAULT_CRACK_SAMPLE_BY_ARCHETYPE}, for the tail layer. */
export const DEFAULT_TAIL_SAMPLE_BY_ARCHETYPE: Record<string, string> = {
  sidearm: 'sidearm-tail-01',
  magnum: 'magnum-tail-01',
  vector: 'vector-tail-01',
  assault: 'assault-tail-01',
  marksman: 'marksman-tail-01',
  longshot: 'longshot-tail-01',
  breacher: 'breacher-tail-01',
  fang: 'fang-tail-01',
  talon: 'talon-tail-01',
  trinity: 'trinity-tail-01',
  viper: 'viper-tail-01',
  ripple: 'ripple-tail-01',
};

/**
 * Same idea as {@link DEFAULT_CRACK_SAMPLE_BY_ARCHETYPE}, for a melee
 * weapon's swing layer. Only `shiv` has a real sample today; a creator
 * authoring a new melee archetype still gets the generic "type any
 * sampleId" path via {@link WeaponAudioResolver}'s 404-on-miss fallback.
 */
export const DEFAULT_SWING_SAMPLE_BY_ARCHETYPE: Record<string, string> = {
  shiv: 'shiv-swing-01',
};

/** Same idea as {@link DEFAULT_SWING_SAMPLE_BY_ARCHETYPE}, for the impact layer. */
export const DEFAULT_IMPACT_SAMPLE_BY_ARCHETYPE: Record<string, string> = {
  shiv: 'shiv-impact-01',
};

// Falls back to a plain `public/`-relative URL for any sampleId not in the
// bundled set - genuinely fetchable the moment a file lands at that path
// (e.g. once a real curated sample library exists), no code change required,
// and honest about failing (a real 404, surfaced to the creator) rather than
// pretending to succeed when nothing is there.
export const gunsmithAudioResolver: WeaponAudioResolver = {
  sampleUrl(sampleId: string): string {
    return BUNDLED_SAMPLE_URLS[sampleId] ?? `/audio/samples/${sampleId}.mp3`;
  },
};
